import { Tooltip } from '@brightdrop/bd-ui';
import { IconButton } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type SidekickFeatureEventInput,
  useSidekickFeatureEventReporter,
} from '../../featureEvents';
import {
  COPY_STATUS,
  useCopyToClipboard,
} from '../../hooks/useCopyToClipboard';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import { CheckGlyph, CopyGlyph } from '../icons';
import useMessageActionButtonStyles from '../messageActionButton.styles';
import { showSidekickErrorToast } from '../toast';
import useStyles from './MessageCopyButton.styles';

const COPY_ICON_SIZE_PX = 16;

/** Props for the per-message copy action. */
export type MessageCopyButtonProps = {
  /** Exact text written to the clipboard. */
  text: string;
  /**
   * Accessible name for the button. Supplied by the caller because
   * the query and the response need different wording.
   */
  ariaLabel: string;
  /** `data-testid` for the button element. */
  testId: string;
  /** Feature event reported once the text has reached the clipboard. */
  copiedEvent?: SidekickFeatureEventInput;
  className?: string;
};

/**
 * Copies one message to the clipboard.
 *
 * Success is confirmed in place — the glyph swaps to a check on the
 * active brand fill for a brief beat — rather than through a toast,
 * matching the GA-Sidekick design. Because that swap is purely
 * visual, a polite live region announces it for screen readers; the
 * announcement is held longer than the glyph, so the two revert on
 * separate timers. Failure falls back to the shared Sidekick error
 * toast.
 */
function MessageCopyButton({
  text,
  ariaLabel,
  testId,
  copiedEvent,
  className,
}: MessageCopyButtonProps) {
  const { classes } = useStyles();
  const { classes: buttonClasses, cx } = useMessageActionButtonStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const { captureEmit } = useSidekickFeatureEventReporter();
  const { copy, status, isAnnouncingCopy } = useCopyToClipboard();

  const isCopied = status === COPY_STATUS.Copied;

  const handleClick = useCallback(() => {
    const emitCopied = captureEmit();
    // Presentation of the failure lives in the component, not the
    // hook, matching the package convention.
    void copy(text).then((didCopy) => {
      if (!didCopy) {
        showSidekickErrorToast({
          message: t('chat.copy.errorToast'),
          closeAriaLabel: t('chat.copy.errorToastDismissAriaLabel'),
          testId: 'ai-sidekick-copy-error-toast',
        });
        return;
      }
      if (copiedEvent !== undefined) emitCopied(copiedEvent);
    });
  }, [captureEmit, copiedEvent, copy, t, text]);

  return (
    <>
      <Tooltip
        placement="top"
        content={t('chat.copy.tooltip')}
        trigger={
          <IconButton
            className={cx(
              buttonClasses.button,
              isCopied && buttonClasses.buttonActive,
              className
            )}
            onClick={handleClick}
            aria-label={ariaLabel}
            data-testid={testId}
          >
            {isCopied ? (
              <CheckGlyph size={COPY_ICON_SIZE_PX} />
            ) : (
              <CopyGlyph size={COPY_ICON_SIZE_PX} />
            )}
          </IconButton>
        }
      />
      <span className={classes.visuallyHidden} aria-live="polite">
        {isAnnouncingCopy ? t('chat.copy.copiedAnnouncement') : ''}
      </span>
    </>
  );
}

export default MessageCopyButton;
